<script lang="ts" setup>
import { computed, InputHTMLAttributes, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import Dropzone, { DropzoneFile } from 'dropzone'
import { IDSKSummaryListProps } from '/@src/components/base/IDSKSummaryList.vue'

const CLASS_ROOT = 'idsk-drag-and-drop'

export interface IDSKDragAndDropProps extends InputHTMLAttributes {
  id: string
  label?: string
  hint?: string
  hintId?: string
  errorMessage?: string
  errorMessageId?: string
  readOnly?: boolean
  showDefaulSummaryList?: boolean
  disabled?: boolean
}

type IDSKDragAndDropEmits = {
  (e: 'change', value: string[]): void
  (e: 'addedFiles', value: DropzoneFile[]): void
}

const { t } = useI18n()

const props = withDefaults(defineProps<IDSKDragAndDropProps>(), {
  label: undefined,
  hint: undefined,
  hintId: undefined,
  errorMessage: undefined,
  errorMessageId: undefined,
  showDefaulSummaryList: true,
  disabled: false,
})

const dropzone = ref<Dropzone>()
const dropzoneElementRef = ref<HTMLDivElement>()

const files = ref<DropzoneFile[]>([])

onMounted(() => {
  if (dropzoneElementRef.value) {
    dropzone.value = new Dropzone(dropzoneElementRef.value, {
      url: '#',
      autoProcessQueue: false, // don't upload to the server on file add
      previewsContainer: false,
      addedfiles(uploadedFiles) {
        emit('addedFiles', uploadedFiles)
        files.value.push(...Array.from(uploadedFiles))
      },
    })
  }
})

const uploadedFilesList = computed<IDSKSummaryListProps['data']>(() =>
  files.value?.reduce((acc, file) => {
    acc[file.name] = undefined
    return acc
  }, {} as NonNullable<IDSKSummaryListProps['data']>)
)

const emit = defineEmits<IDSKDragAndDropEmits>()

watch(files, () => {
  const fileIds = files.value.map((file) => file.name)
  emit('change', fileIds)
})

const isValid = computed<boolean>(() => !props.errorMessage)

const handleFileRemove = (val: string) => {
  files.value = files.value.filter((file) => file.name !== val)
}
</script>

<template>
  <div
    :class="[
      'govuk-form-group',
      {
        'govuk-form-group--error': !isValid,
      },
    ]"
  >
    <IDSKFieldCaption
      :label="label"
      :for="props?.id"
      :hint="hint"
      :hint-id="hintId"
      :error-message="errorMessage"
      :error-message-id="errorMessageId"
    />
    <IDSKSummaryList
      v-if="showDefaulSummaryList"
      :data="uploadedFilesList"
      action-text="Odstrániť"
      @action="handleFileRemove"
    />
    <div
      v-if="!disabled"
      :id="props?.id"
      ref="dropzoneElementRef"
      :class="[CLASS_ROOT, 'govuk-body']"
    >
      <div class="dz-message" data-dz-message>
        <div :class="`${CLASS_ROOT}__area`">
          <p :class="`${CLASS_ROOT}__label`">{{ t('label') }}</p>
          <IDSKButton
            :data-test-id="`vyberte-subor-${props.id}`"
            variant="secondary"
            :class="[`${CLASS_ROOT}__button`, 'mb-0']"
            @click.prevent
          >
            {{ t('vyberteSubor') }}
          </IDSKButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.idsk-drag-and-drop {
  &__label {
    margin-right: 1rem;
    margin-bottom: 0;
  }

  &__area {
    cursor: pointer;
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 2rem;
    width: 100%;
    height: 157px;
    background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%23333' stroke-width='4' stroke-dasharray='14%2c 20' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");

    @media (max-width: 767px) {
      flex-direction: column;
    }
  }

  & .dz-message {
    display: flex;
    margin-bottom: 1.5rem;
  }
}
</style>

<i18n lang="yaml">
sk:
  vyberteSubor: 'Vyberte súbor'
  label: 'Potiahnite sem súbory'
</i18n>
